/**
 * PieChart Component - Pie chart for data distributions and percentages
 */

import React, { useCallback } from 'react';
import { BaseChart, ChartDataPoint, ChartConfig } from './BaseChart';

export interface PieChartProps extends ChartConfig {
  data: any;
  options?: any; // Accept Chart.js-style options for compatibility
  className?: string;
  onDataPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
  onDataPointHover?: (dataPoint: ChartDataPoint | null, index: number) => void;
  loading?: boolean;
  error?: string;

  // Pie-specific props
  showLabels?: boolean;
  showPercentages?: boolean;
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  innerRadius?: number; // For donut charts
  startAngle?: number;
  colors?: string[];
  explodeOnHover?: boolean;
  explodeDistance?: number;
}

const defaultColors = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#84cc16', // lime
  '#06b6d4', // cyan
  '#6366f1', // indigo
];

export const PieChart: React.FC<PieChartProps> = ({
  data,
  options, // Accepted but ignored for Chart.js compatibility
  className = '',
  onDataPointClick,
  onDataPointHover,
  loading = false,
  error,
  showLabels = true,
  showPercentages = true,
  showLegend = false,
  legendPosition = 'right',
  innerRadius = 0,
  startAngle = -Math.PI / 2,
  colors = defaultColors,
  explodeOnHover = true,
  explodeDistance = 10,
  ...configProps
}) => {
  const customRender = useCallback((
    ctx: CanvasRenderingContext2D,
    chartArea: { x: number; y: number; width: number; height: number },
    dataRanges: { xMin: number; xMax: number; yMin: number; yMax: number },
    dataToCanvas: (dataX: number, dataY: number) => { x: number; y: number }
  ) => {
    if (data.length === 0) return;

    const centerX = chartArea.x + chartArea.width / 2;
    const centerY = chartArea.y + chartArea.height / 2;
    const maxRadius = Math.min(chartArea.width, chartArea.height) / 2 - 20;
    const outerRadius = showLegend ? maxRadius * 0.8 : maxRadius;

    // Calculate total value
    const total = data.reduce((sum, item) => sum + item.y, 0);

    // Draw pie slices
    let currentAngle = startAngle;
    
    data.forEach((dataPoint, index) => {
      const value = dataPoint.y;
      const sliceAngle = (value / total) * 2 * Math.PI;
      const color = dataPoint.color || colors[index % colors.length];
      
      // Calculate slice center for explosion effect
      const midAngle = currentAngle + sliceAngle / 2;
      let sliceCenterX = centerX;
      let sliceCenterY = centerY;
      
      // Apply explosion effect if this slice is hovered (would need hover state management)
      // For now, we'll skip the explosion effect in this implementation
      
      ctx.save();
      
      // Draw slice
      ctx.beginPath();
      ctx.arc(sliceCenterX, sliceCenterY, outerRadius, currentAngle, currentAngle + sliceAngle);
      
      if (innerRadius > 0) {
        // Donut chart - draw inner arc
        ctx.arc(sliceCenterX, sliceCenterY, innerRadius, currentAngle + sliceAngle, currentAngle, true);
      } else {
        // Pie chart - line to center
        ctx.lineTo(sliceCenterX, sliceCenterY);
      }
      
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();

      // Add stroke
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw labels if enabled
      if (showLabels || showPercentages) {
        const labelRadius = outerRadius * 0.7;
        const labelX = sliceCenterX + Math.cos(midAngle) * labelRadius;
        const labelY = sliceCenterY + Math.sin(midAngle) * labelRadius;

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.strokeStyle = '#333333';
        ctx.lineWidth = 3;

        let labelText = '';
        if (showLabels && showPercentages) {
          const percentage = ((value / total) * 100).toFixed(1);
          labelText = `${dataPoint.label || ''}\n${percentage}%`;
        } else if (showPercentages) {
          const percentage = ((value / total) * 100).toFixed(1);
          labelText = `${percentage}%`;
        } else if (showLabels) {
          labelText = dataPoint.label || '';
        }

        // Draw text with outline for better visibility
        if (labelText) {
          const lines = labelText.split('\n');
          lines.forEach((line, lineIndex) => {
            const lineY = labelY + (lineIndex - (lines.length - 1) / 2) * 14;
            ctx.strokeText(line, labelX, lineY);
            ctx.fillText(line, labelX, lineY);
          });
        }
      }

      ctx.restore();
      currentAngle += sliceAngle;
    });

    // Draw legend if enabled
    if (showLegend) {
      const legendItems = data.map((item, index) => ({
        color: item.color || colors[index % colors.length],
        label: item.label || `Item ${index + 1}`,
        value: item.y,
        percentage: ((item.y / total) * 100).toFixed(1),
      }));

      ctx.save();
      ctx.font = '12px sans-serif';
      ctx.textBaseline = 'middle';

      const legendItemHeight = 20;
      const legendItemWidth = 120;
      const legendPadding = 10;

      let legendX: number, legendY: number;

      switch (legendPosition) {
        case 'top':
          legendX = chartArea.x;
          legendY = chartArea.y - legendItems.length * legendItemHeight - legendPadding;
          break;
        case 'bottom':
          legendX = chartArea.x;
          legendY = chartArea.y + chartArea.height + legendPadding;
          break;
        case 'left':
          legendX = chartArea.x - legendItemWidth - legendPadding;
          legendY = chartArea.y;
          break;
        case 'right':
        default:
          legendX = chartArea.x + chartArea.width + legendPadding;
          legendY = chartArea.y;
          break;
      }

      legendItems.forEach((item, index) => {
        const itemY = legendY + index * legendItemHeight;

        // Color box
        ctx.fillStyle = item.color;
        ctx.fillRect(legendX, itemY, 12, 12);

        // Label and value
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'left';
        const labelText = `${item.label} (${item.percentage}%)`;
        ctx.fillText(labelText, legendX + 18, itemY + 6);
      });

      ctx.restore();
    }
  }, [data, showLabels, showPercentages, showLegend, legendPosition, innerRadius, startAngle, colors, explodeOnHover, explodeDistance]);

  return (
    <BaseChart
      data={data}
      className={className}
      onDataPointClick={onDataPointClick}
      onDataPointHover={onDataPointHover}
      loading={loading}
      error={error}
      customRender={customRender}
      showGrid={false} // Pie charts don't need grid
      {...configProps}
    />
  );
};